// Client-side TikTok Pixel event helper.
// ----------------------------------------------------------------------------
// Thin, crash-proof wrapper over the `ttq` global injected by
// src/components/analytics/TikTokPixel.tsx. No-ops anywhere the pixel isn't
// loaded (dev, missing env, ad-blocked, SSR). Pass an `eventId` to deduplicate
// against the server-side Events API fire for the same conversion (TikTok dedups
// natively on a shared event_id).

type TtqIdentity = {
  email?: string;
  phone_number?: string;
  external_id?: string;
};

type Ttq = {
  track: (event: string, params?: Record<string, unknown>, options?: { event_id?: string }) => void;
  page: (params?: Record<string, unknown>) => void;
  identify: (identity: TtqIdentity) => void;
};

function getTtq(): Partial<Ttq> | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { ttq?: Partial<Ttq> }).ttq;
}

// --- pixel-readiness queue --------------------------------------------------
//
// The pixel <Script> is injected with strategy="afterInteractive", so it can
// execute AFTER React has hydrated and run effects. Anything fired from a mount
// effect — notably the first ViewContent, which is the ad landing itself, and
// the identify that gives every later event its match keys — would hit an
// undefined `ttq` and vanish. So calls made before the pixel exists are queued
// and flushed in order once it appears.
//
// Bounded on purpose: when the pixel is ad-blocked or disabled it never
// arrives, and an unbounded poller would leak a timer for the whole session.
const READY_TIMEOUT_MS = 8000;
const READY_POLL_MS = 200;

let pendingCalls: Array<(ttq: Partial<Ttq>) => void> = [];
let readyPoller: ReturnType<typeof setInterval> | null = null;
let waitedMs = 0;

function stopPolling(): void {
  if (readyPoller !== null) {
    clearInterval(readyPoller);
    readyPoller = null;
  }
}

function withTtq(fn: (ttq: Partial<Ttq>) => void): void {
  const ttq = getTtq();
  if (ttq) {
    fn(ttq);
    return;
  }
  if (typeof window === "undefined") return;

  pendingCalls.push(fn);
  if (readyPoller !== null) return;

  readyPoller = setInterval(() => {
    waitedMs += READY_POLL_MS;
    const ready = getTtq();
    if (ready) {
      const queued = pendingCalls;
      pendingCalls = [];
      stopPolling();
      for (const call of queued) {
        try {
          call(ready);
        } catch {
          // one bad queued call must not drop the rest
        }
      }
      return;
    }
    if (waitedMs >= READY_TIMEOUT_MS) {
      // Pixel never showed up (blocked, or not configured) — drop the backlog
      // rather than hold a timer and stale closures for the session.
      pendingCalls = [];
      stopPolling();
    }
  }, READY_POLL_MS);
}

export function trackTikTokClientEvent(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string,
): void {
  try {
    withTtq((ttq) => {
      if (typeof ttq.track !== "function") return;
      ttq.track(event, params ?? {}, eventId ? { event_id: eventId } : undefined);
    });
  } catch {
    // never let analytics throw into product code
  }
}

/**
 * SHA-256 → lowercase hex, via WebCrypto.
 *
 * Returns undefined rather than throwing on an insecure origin, where
 * `crypto.subtle` is undefined — sending a raw email because hashing was
 * unavailable would leak PII, so we send nothing instead.
 */
async function sha256Hex(value: string): Promise<string | undefined> {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return undefined;
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
}

/**
 * Advanced Matching: attach the signed-in user's identifiers to every
 * subsequent pixel event.
 *
 * Without this, client events carry only the anonymous `_ttp` cookie, which is
 * why an ad-blocked or cross-device visitor never joins back to the server
 * Events API fire for the same person.
 *
 * Per TikTok's Events Manager implementation template, email / phone_number /
 * external_id MUST be SHA-256 hashed on the client — raw PII never leaves the
 * page. The normalization below (trim + lowercase for email/external_id, E.164
 * for phone) is deliberately IDENTICAL to the server hashing in
 * `src/lib/tracking/tiktok.ts`; if the two ever diverge the digests stop
 * matching and client/server events silently fail to join to one person.
 *
 * Async because WebCrypto is. Safe to call repeatedly and never rejects, so
 * callers can fire-and-forget with `void`.
 */
export async function identifyTikTokUser(identity: {
  email?: string | null;
  /** Any format; normalized to E.164 before hashing. */
  phone?: string | null;
  externalId?: string | null;
}): Promise<void> {
  try {
    const payload: TtqIdentity = {};

    const email = identity.email?.trim().toLowerCase();
    if (email && email.includes("@")) {
      const hashed = await sha256Hex(email);
      if (hashed) payload.email = hashed;
    }

    // E.164 ("+15551234567") before hashing — a differently-formatted string
    // produces a digest that can never match TikTok's side.
    const digits = (identity.phone ?? "").replace(/\D/g, "");
    if (digits.length >= 8) {
      const hashed = await sha256Hex(`+${digits}`);
      if (hashed) payload.phone_number = hashed;
    }

    const externalId = identity.externalId?.trim().toLowerCase();
    if (externalId) {
      const hashed = await sha256Hex(externalId);
      if (hashed) payload.external_id = hashed;
    }

    if (Object.keys(payload).length === 0) return;
    withTtq((ttq) => {
      if (typeof ttq.identify !== "function") return;
      ttq.identify(payload);
    });
  } catch {
    // never let analytics throw into product code
  }
}

/**
 * Report a pageview.
 *
 * The base pixel snippet calls `ttq.page()` exactly once, on script load. Every
 * screen after that is a Next.js client-side navigation that swaps the URL
 * without reloading the document — so without this the pixel only ever sees the
 * landing page, and TikTok's view-through attribution and retargeting audiences
 * miss the entire in-app journey.
 */
export function trackTikTokPageView(): void {
  try {
    withTtq((ttq) => {
      if (typeof ttq.page !== "function") return;
      ttq.page();
    });
  } catch {
    // never let analytics throw into product code
  }
}
